import { useState } from 'react'

import {
  useCreateReviewFieldApi,
  useDeleteReviewFieldApi,
  useReviewFieldsApi
} from '@/common/api/modules/ugcReviewSetup'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'

/**
 * 投稿フォームのカスタム項目（肌タイプ・サイズ感など）。
 *
 * 投稿フォーム側は項目の描画・保存に以前から対応していたが、
 * **項目を定義するこの画面が存在しなかった**（APIのみ）。
 * 使えるのに設定できない機能は、無いのと同じになっていた。
 */

const input =
  'shadow-inset text-size-13 w-full rounded-lg border border-border-input bg-input px-3 py-2'

const FIELD_TYPES = [
  { value: 'select', label: '選択式', hint: '選択肢をカンマ区切りで入力します' },
  { value: 'text', label: '自由記述', hint: '' },
  { value: 'number', label: '数値', hint: '' }
] as const

type Draft = {
  field_name: string
  field_type: 'select' | 'text' | 'number'
  optionsText: string
  is_required: boolean
}

const emptyDraft = (): Draft => ({
  field_name: '',
  field_type: 'select',
  optionsText: '',
  is_required: false
})

const ReviewFieldEditor = ({ siteId }: { siteId: number }) => {
  const { data, isLoading, mutate } = useReviewFieldsApi({ site_id: siteId })
  const [draft, setDraft] = useState<Draft | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const { trigger: create, isMutating: creating } = useCreateReviewFieldApi({
    onSuccess: () => {
      setDraft(null)
      setError(null)
      mutate()
    },
    onError: (e) => setError(e instanceof Error ? e.message : '保存できませんでした')
  })
  const { trigger: remove } = useDeleteReviewFieldApi({
    onSuccess: () => {
      setDeletingId(null)
      mutate()
    },
    onError: () => setDeletingId(null)
  })

  const onSave = () => {
    if (!draft) return
    if (!draft.field_name.trim()) {
      setError('項目名を入力してください')
      return
    }
    const options = draft.optionsText
      .split(/[,、]/)
      .map((v) => v.trim())
      .filter(Boolean)
    if (draft.field_type === 'select' && options.length < 2) {
      setError('選択式には選択肢を2つ以上入力してください')
      return
    }
    setError(null)
    create({
      site_id: siteId,
      field_name: draft.field_name.trim(),
      field_type: draft.field_type,
      options: draft.field_type === 'select' ? options : null,
      is_required: draft.is_required,
      display_order: (data?.fields.length ?? 0) + 1
    })
  }

  const fields = data?.fields ?? []

  return (
    <Area>
      <Heading subText='星と本文に加えて、投稿フォームで聞く項目です。肌タイプ・サイズ感など、商品に合わせて足せます。'>
        投稿フォームの項目
      </Heading>
      <AreaContent>
        {isLoading ? (
          <Text color={TextColor.SECONDARY}>読み込み中...</Text>
        ) : (
          <div className='flex flex-col gap-4'>
            {fields.length === 0 && !draft && (
              <Text size={13} color={TextColor.SECONDARY}>
                カスタム項目はありません。フォームは星と本文だけになります。
              </Text>
            )}

            {fields.length > 0 && (
              <div className='flex flex-col gap-2'>
                {fields.map((f) => (
                  <div
                    key={f.id}
                    className='flex flex-wrap items-center gap-3 rounded-lg border border-border-primary px-4 py-3'
                  >
                    <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                      <Text size={13} weight='bold'>
                        {f.field_name}
                        {f.is_required ? '（必須）' : ''}
                      </Text>
                      <Text size={10} color={TextColor.TERTIARY}>
                        {FIELD_TYPES.find((t) => t.value === f.field_type)?.label ?? f.field_type}
                        {f.options.length > 0 ? `: ${f.options.join(' / ')}` : ''}
                      </Text>
                    </div>
                    <Button
                      color={ButtonColor.TERTIARY}
                      disabled={deletingId === f.id}
                      loading={deletingId === f.id}
                      onClick={() => {
                        setDeletingId(f.id)
                        remove({ site_id: siteId, field_id: f.id })
                      }}
                    >
                      削除
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {draft ? (
              <div className='flex flex-col gap-3 rounded-lg border border-border-brand px-4 py-4'>
                <div className='grid gap-3 md:grid-cols-2'>
                  <label className='flex flex-col gap-1'>
                    <Text size={12}>項目名</Text>
                    <input
                      className={input}
                      value={draft.field_name}
                      placeholder='例）肌タイプ'
                      onChange={(e) => setDraft({ ...draft, field_name: e.target.value })}
                    />
                  </label>
                  <label className='flex flex-col gap-1'>
                    <Text size={12}>回答の形式</Text>
                    <select
                      className={input}
                      value={draft.field_type}
                      onChange={(e) =>
                        setDraft({ ...draft, field_type: e.target.value as Draft['field_type'] })
                      }
                    >
                      {FIELD_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                {draft.field_type === 'select' && (
                  <label className='flex flex-col gap-1'>
                    <Text size={12}>選択肢（カンマ区切り）</Text>
                    <input
                      className={input}
                      value={draft.optionsText}
                      placeholder='例）乾燥肌, 普通肌, 脂性肌, 混合肌'
                      onChange={(e) => setDraft({ ...draft, optionsText: e.target.value })}
                    />
                  </label>
                )}
                <label className='flex items-center gap-2'>
                  <input
                    type='checkbox'
                    checked={draft.is_required}
                    onChange={(e) => setDraft({ ...draft, is_required: e.target.checked })}
                  />
                  <Text size={12}>回答を必須にする</Text>
                </label>
                <div className='flex flex-wrap items-center gap-3'>
                  <Button
                    color={ButtonColor.PRIMARY}
                    loading={creating}
                    disabled={creating}
                    onClick={onSave}
                  >
                    追加する
                  </Button>
                  <Button color={ButtonColor.TERTIARY} onClick={() => setDraft(null)}>
                    やめる
                  </Button>
                  {error && (
                    <Text size={12} color={TextColor.CRITICAL}>
                      {error}
                    </Text>
                  )}
                </div>
                <Text size={10} color={TextColor.TERTIARY}>
                  必須項目を増やすほど投稿は減ります。まず任意で足して、回答率を見てから
                  必須にすることをおすすめします。
                </Text>
              </div>
            ) : (
              <div>
                <Button color={ButtonColor.SECONDARY} onClick={() => setDraft(emptyDraft())}>
                  項目を追加する
                </Button>
              </div>
            )}
          </div>
        )}
      </AreaContent>
    </Area>
  )
}

export default ReviewFieldEditor
